import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, BookOpen, NotebookPen, Clock, Target, Users, Layers, ShieldAlert } from "lucide-react";
import { META, NOTES } from "../data/ks2_teacher_guide";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mt-7">
      <h3 className="font-display text-lg text-navy inline-flex items-center gap-2">
        <Icon size={16} className="text-saffron" /> {title}
      </h3>
      <div className="mt-2 text-navy/85 leading-relaxed text-[15px]">{children}</div>
    </div>
  );
}

export default function Ks2TeacherGuide() {
  const [active, setActive] = useState(NOTES[0].n);
  const current = NOTES.find((n) => n.n === active) || NOTES[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [active]);

  return (
    <div className="bg-pearl min-h-screen" data-testid="ks2-teacher-guide">
      <style>{`@media print { .no-print { display: none !important; } .print-section { page-break-after: always; } body { background: white; } }`}</style>

      <header className="bg-navy text-pearl py-10 px-4 sm:px-8 no-print">
        <div className="max-w-6xl mx-auto">
          <Link to="/education" className="inline-flex items-center gap-2 text-saffron text-xs uppercase tracking-widest hover:text-saffron-light mb-5">
            <ArrowLeft size={14} /> Back to Education
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-saffron">{META.series}</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{META.title}</h1>
          <p className="text-pearl/70 mt-2">{META.subtitle}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => window.print()}
              data-testid="ks2-teacher-print"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron/90"
            >
              <Printer size={16} /> Print this lesson
            </button>
            <Link to="/education/ks2-book" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 border border-white/20 text-pearl rounded-md text-sm hover:bg-white/15">
              <BookOpen size={16} /> Open Student Book
            </Link>
            <Link to="/education/ks2-workbook" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 border border-white/20 text-pearl rounded-md text-sm hover:bg-white/15">
              <NotebookPen size={16} /> Open Workbook
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <aside className="no-print">
          <div className="lg:sticky lg:top-24">
            <div className="text-[10px] uppercase tracking-widest text-saffron mb-3">Lessons</div>
            <ul className="space-y-1.5" data-testid="ks2-teacher-index">
              {NOTES.map((n) => (
                <li key={n.n}>
                  <button
                    onClick={() => setActive(n.n)}
                    data-testid={`ks2-teacher-link-${n.n}`}
                    className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors ${
                      active === n.n ? "bg-navy text-pearl" : "text-navy/70 hover:bg-saffron/10 hover:text-saffron"
                    }`}
                  >
                    <span className="font-display text-saffron mr-2">{n.n}</span>
                    {n.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="bg-white rounded-lg border border-navy/10 shadow-sm p-6 sm:p-10 print-section" data-testid={`ks2-teacher-note-${current.n}`}>
          <div className="border-b border-navy/10 pb-5 mb-2">
            <p className="text-[10px] uppercase tracking-widest text-saffron">Lesson {current.n} of {NOTES.length}</p>
            <h2 className="font-display text-3xl text-navy mt-1">{current.title}</h2>
            <p className="mt-3 text-navy/75 leading-relaxed">{current.aim}</p>
          </div>

          <Section icon={Clock} title="60-minute plan">
            <table className="w-full text-sm border-collapse">
              <tbody>
                {current.plan.map((p, i) => (
                  <tr key={i} className="border-b border-navy/10">
                    <td className="py-2 pr-4 text-saffron font-medium whitespace-nowrap align-top w-24">{p.mins}</td>
                    <td className="py-2 text-navy/85">{p.step}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section icon={Target} title="Discussion prompts">
            <ul className="list-disc pl-5 space-y-1.5">
              {current.discuss.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </Section>

          <Section icon={Layers} title="Differentiation">
            <p>{current.differentiation}</p>
          </Section>

          <Section icon={Users} title="Cross-curricular links">
            <p>{current.cross}</p>
          </Section>

          <Section icon={NotebookPen} title="Assessment cue">
            <p>{current.assess}</p>
          </Section>

          {current.safeguard && (
            <Section icon={ShieldAlert} title="Safeguarding & sensitivity">
              <p className="text-navy/85 italic">{current.safeguard}</p>
            </Section>
          )}

          <div className="mt-10 pt-6 border-t border-navy/10 flex justify-between text-xs text-navy/50">
            <span>{META.title}</span>
            <span>{META.edition}</span>
          </div>
        </article>
      </div>
    </div>
  );
}
